import { Table,Tag } from "antd";
import {
  getJobDriveAttendanceApi,
  getJobListApi
} from "api/services/jobs";
import Breadcrumbs from "components/Breadcrumbs";
import { SkeltonLoader } from "components/skeltonLoader";
import { format,parseISO } from "date-fns";
import { useEffect,useMemo,useState } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import Select from "react-select";
import { Bounce,toast } from "react-toastify";
import { customStyleSelect } from "utils/common";
import {
  itemRender,
  pageSizeOptions,
} from "utils/table";

const JobDriveAttendance = () => {
  const { user, userLoading } = useSelector((state) => state.user);
  
  // Check if user has job_drive_attendance permission
  const hasJobDrivePermission = useMemo(() => {
    // Wait until user is fully loaded
    if (userLoading) return null; // null means "still checking"
    // Check if user object is empty (initial state is {})
    if (!user || Object.keys(user).length === 0 || !user.id) return null; // Still waiting for user data
    // If user has no role or modules, they don't have permission
    if (!user?.role?.permissions && !user?.modules) return false;
    // Check for job_drive_attendance permission
    return user.role?.permissions?.some((p) => p.code === "job_drive_attendance") ||
           user?.modules?.some?.((m) => m.code === "job_drive_attendance");
  }, [user, userLoading]);

  const [loading, setLoading] = useState(false);
  const [attendance, setAttendance] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [pageSettings, setPageSettings] = useState({
    page: 1,
    pageSize: 50,
    total: 0,
    sortBy: "",
    sortType: "",
  });

  // Fetch jobs with job_drive_enabled
  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    setJobsLoading(true);
    try {
      const response = await getJobListApi({ job_drive_enabled: true });
      setJobs(response.data?.results || []);
    } catch (error) {
      console.error("Error fetching jobs:", error);
      toast.error("Failed to load jobs", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setJobsLoading(false);
    }
  };

  const fetchAttendance = async (page = 1, pageSize = 50, jobId = null, sortBy = "", sortType = "") => {
    setLoading(true);
    try {
      const params = {
        page,
        size: pageSize,
      };

      if (jobId) {
        params.job_id = jobId;
      }

      if (sortBy) {
        params.sort_by = sortBy;
        params.sort_type = sortType || "asc";
      }

      const response = await getJobDriveAttendanceApi(params);
      
      // Handle different response structures
      const attendanceData = response.data;
      let attendanceArray = [];
      if (Array.isArray(attendanceData)) {
        attendanceArray = attendanceData;
      } else if (attendanceData?.results && Array.isArray(attendanceData.results)) {
        attendanceArray = attendanceData.results;
      } else if (attendanceData?.attendance && Array.isArray(attendanceData.attendance)) {
        attendanceArray = attendanceData.attendance;
      }
      
      setAttendance(attendanceArray);
      
      // Update pagination
      if (attendanceData?.count !== undefined) {
        setPageSettings(prev => ({
          ...prev,
          total: attendanceData.count,
          page,
          pageSize
        }));
      } else if (attendanceData?.total !== undefined) {
        setPageSettings(prev => ({
          ...prev,
          total: attendanceData.total,
          page,
          pageSize
        }));
      }
    } catch (error) {
      console.error("Error fetching attendance:", error);
      setAttendance([]);
      toast.error("Failed to load attendance data", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendance(
      pageSettings.page,
      pageSettings.pageSize,
      selectedJob?.value || null,
      pageSettings.sortBy,
      pageSettings.sortType
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJob, pageSettings.page, pageSettings.pageSize, pageSettings.sortBy, pageSettings.sortType]);

  const handleTableChange = (pagination, filters, sorter) => {
    const newPage = pagination.current;
    const newPageSize = pagination.pageSize;
    let newSortBy = "";
    let newSortType = "";

    if (sorter && sorter.field) {
      newSortBy = sorter.field;
      newSortType = sorter.order === "ascend" ? "asc" : sorter.order === "descend" ? "desc" : "";
    }

    setPageSettings(prev => ({
      ...prev,
      page: newPage,
      pageSize: newPageSize,
      sortBy: newSortBy,
      sortType: newSortType,
    }));
  };

  // Wait for user to load before checking permissions
  if (userLoading || hasJobDrivePermission === null) {
    return <div>Loading...</div>;
  }

  // Redirect if no permission (after hooks)
  if (hasJobDrivePermission === false) {
    return <Navigate to="/manage-jobs" replace />;
  }

  // Prepare job options
  const jobOptions = jobs?.map(job => ({
    label: job.title,
    value: job.id
  })) || [];

  const attendanceColumns = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      sorter: true,
      width: 80,
    },
    {
      title: "Applicant Name",
      key: "applicant_name",
      render: (_, record) => {
        const applicant = record.invitation?.applicant;
        return applicant ? `${applicant.first_name || ""} ${applicant.last_name || ""}`.trim() || "-" : "-";
      },
      sorter: true,
    },
    {
      title: "Email",
      key: "email",
      render: (_, record) => {
        const applicant = record.invitation?.applicant;
        return applicant?.email || record.invitation?.email_to || "-";
      },
      sorter: true,
    },
    {
      title: "Phone",
      key: "phone",
      render: (_, record) => {
        const applicant = record.invitation?.applicant;
        return applicant?.phone || applicant?.mobile || "-";
      },
    },
    {
      title: "Time Slot",
      key: "time_slot",
      render: (_, record) => {
        if (record.invitation?.slot) {
          const slot = record.invitation.slot;
          try {
            const startDate = parseISO(slot.start_at);
            const endDate = parseISO(slot.end_at);
            return (
              <div>
                <div>{slot.title || "-"}</div>
                <div style={{ fontSize: "12px", color: "#666" }}>
                  {format(startDate, "dd-MM-yyyy HH:mm")} - {format(endDate, "HH:mm")}
                </div>
                {slot.location && (
                  <div style={{ fontSize: "11px", color: "#999" }}>
                    {slot.location}
                  </div>
                )}
              </div>
            );
          } catch (error) {
            return slot.title || "-";
          }
        }
        return <Tag color="default">Not Assigned</Tag>;
      },
    },
    {
      title: "Scanned By",
      key: "scanned_by",
      render: (_, record) => {
        const scannedBy = record.scanned_by?.user;
        return scannedBy ? `${scannedBy.first_name || ""} ${scannedBy.last_name || ""}`.trim() || scannedBy.email || "-" : "-";
      },
      sorter: true,
    },
    {
      title: "Scanned At",
      dataIndex: "scanned_at",
      key: "scanned_at",
      render: (text) => {
        if (text) {
          try {
            return format(parseISO(text), "dd-MM-yyyy HH:mm:ss");
          } catch (error) {
            return text;
          }
        }
        return "-";
      },
      sorter: true,
    },
    {
      title: "Result",
      dataIndex: "result",
      key: "result",
      render: (text) => {
        if (text === "valid") {
          return <Tag color="success">Valid</Tag>;
        } else if (text === "duplicate") {
          return <Tag color="warning">Duplicate</Tag>;
        }
        return <Tag color="default">{text || "-"}</Tag>;
      },
      sorter: true,
    },
    {
      title: "Message",
      dataIndex: "message",
      key: "message",
      render: (text) => text || "-",
    },
    {
      title: "IP Address",
      dataIndex: "ip_address",
      key: "ip_address",
      render: (text) => text || "-",
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <Breadcrumbs
          maintitle="Job Drive Attendance Logs"
          title="Attendance"
          subtitle="Job Drive"
          hideGrid={true}
        />
        
        <div className="row mb-4">
          <div className="col-lg-4 col-md-12">
            <div className="input-block mb-3">
              {/* <label className="col-form-label">Filter by Job</label> */}
              <Select
                placeholder="Select a job"
                value={selectedJob}
                onChange={(option) => {
                  setSelectedJob(option);
                  setPageSettings(prev => ({ ...prev, page: 1 }));
                }}
                options={jobOptions}
                styles={customStyleSelect}
                isClearable
                isLoading={jobsLoading}
              />
            </div>
          </div>
        </div>
        
        <div className="row">
          <div className="col-12">
            <div className="card shadow-sm" style={{ borderRadius: "12px", border: "1px solid #e0e0e0", overflow: "hidden" }}>
              <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                <style>{`
                  .modern-table .ant-table-tbody > tr {
                    background-color: #ffffff !important;
                  }
                  .modern-table .ant-table-tbody > tr:hover > td {
                    background-color: #f5f5f5 !important;
                  }
                  .modern-table .ant-table-thead > tr > th {
                    background-color: #f8f9fa !important;
                    font-weight: 600;
                    color: #333;
                    border-bottom: 2px solid #e0e0e0;
                    padding: 10px 12px;
                    font-size: 13px;
                  }
                  .modern-table .ant-table-tbody > tr > td {
                    border-bottom: 1px solid #f0f0f0;
                    padding: 10px 12px;
                    font-size: 13px;
                  }
                  .modern-table .ant-table-wrapper .ant-table-pagination.ant-pagination {
                    border-top: 1px solid #f0f0f0;
                    background-color: #ffffff;
                    border-bottom-left-radius: 12px;
                    border-bottom-right-radius: 12px;
                  }
                  .modern-table .ant-table-wrapper,
                  .modern-table .ant-table-container {
                    overflow-x: auto !important;
                  }
                  .modern-table .ant-table {
                    min-width: 100%;
                  }
                `}</style>
                {loading ? (
                  <SkeltonLoader number={5} active={true} />
                ) : (
                  <Table
                    className="modern-table"
                    columns={attendanceColumns}
                    dataSource={Array.isArray(attendance) ? attendance : []}
                    rowKey="id"
                    scroll={{ x: "max-content" }}
                    pagination={{
                      total: pageSettings?.total,
                      pageSize: pageSettings?.pageSize,
                      showSizeChanger: true,
                      pageSizeOptions: pageSizeOptions,
                      itemRender: itemRender,
                      position: "right",
                      current: pageSettings?.page,
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} of ${total} items`,
                      style: { padding: "16px" }
                    }}
                    onChange={handleTableChange}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobDriveAttendance;
